import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/src/renderer/components/ui/Button'
import { isEmptyTestDataCount, useTestDataActions, type TestDataCount } from '@/src/renderer/lib/testData'
import { TestDataConfirmDialog, testDataCountTexts, useTestDataFlow, type TestDataOutcome } from '../../../TestData'
import { SettingsRow, SettingsSection } from '../../primitives'
import './TestDataSection.scss'

/**
 * **Testdaten** unter Daten & Privatsphäre (#269, Beta) — anlegen und wieder entfernen, mit Rückfrage.
 * Das Ergebnis steht als Statuszeile darunter: Das Einstellungsfenster von Electron hat keinen Toaster.
 * Ohne `TestDataActionsProvider` (Stories ohne Attrappe) zeigt sich der Abschnitt nicht.
 */
export function TestDataSection() {
  const { t } = useTranslation()
  const actions = useTestDataActions()
  const flow = useTestDataFlow(actions)
  const [count, setCount] = useState<TestDataCount | null>(null)

  // Neu zählen beim Öffnen, nach jedem Lauf und wenn das Fenster den Fokus zurückbekommt — das Hilfe-Menü
  // im Hauptfenster legt ebenfalls an.
  useEffect(() => {
    if (!actions) return
    let alive = true
    const recount = () => {
      actions.count().then(next => { if (alive) setCount(next) }, () => {})
    }
    recount()
    window.addEventListener('focus', recount)
    return () => {
      alive = false
      window.removeEventListener('focus', recount)
    }
  }, [actions, flow.outcome])

  if (!actions) return null

  const status = flow.busy
    ? { text: t(flow.busy === 'create' ? 'testData.creating' : 'testData.removing'), error: false }
    : flow.outcome && outcomeStatus(flow.outcome, t)

  return (
    <SettingsSection title={t('testData.sectionTitle')} variant="card" className="test-data-section">
      <SettingsRow
        label={t('testData.create')}
        description={t('testData.sectionDescription')}
        control={
          <Button variant="outline" disabled={flow.busy != null} onClick={() => void flow.ask('create')}>
            {t('testData.createConfirm')}
          </Button>
        }
      />
      <SettingsRow
        label={t('testData.remove')}
        control={
          <Button
            variant="outline"
            disabled={flow.busy != null || count == null || isEmptyTestDataCount(count)}
            onClick={() => void flow.ask('remove')}
          >
            {t('testData.removeConfirm')}
          </Button>
        }
      />
      {status && (
        <p className={`test-data-section__status${status.error ? ' test-data-section__status--error' : ''}`} role="status">
          {status.text}
        </p>
      )}
      <TestDataConfirmDialog {...flow.dialog} />
    </SettingsSection>
  )
}

function outcomeStatus(outcome: TestDataOutcome, t: ReturnType<typeof useTranslation>['t']) {
  switch (outcome.kind) {
    case 'created':
      return { text: `${t('testData.created')} · ${t('testData.createdText', { ...testDataCountTexts(t, outcome.created), seed: outcome.created.seed })}`, error: false }
    case 'removed':
      return { text: `${t('testData.removed')} · ${t('testData.removedText', testDataCountTexts(t, outcome.removed))}`, error: false }
    case 'none':
      return { text: t('testData.none'), error: false }
    case 'failed':
      return { text: t(outcome.action === 'create' ? 'testData.createFailed' : 'testData.removeFailed'), error: true }
  }
}
